import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock, Mail } from 'lucide-react';
import { loginSchema, type LoginInput } from '@gbv/types';
import { AuthButton, AuthError, AuthField, AuthLayout, AuthLink } from '@gbv/ui';
import { useAuth } from '../stores/auth';
import { ApiRequestError } from '../lib/api';

export function LoginPage() {
  const login = useAuth((s) => s.login);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    // The same schema the API validates against, so the two cannot drift.
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: LoginInput): Promise<void> {
    setFormError(null);
    try {
      await login(values.email, values.password);
    } catch (err) {
      setFormError(
        err instanceof ApiRequestError
          ? err.message
          : 'We could not reach the server. Check your connection and try again.',
      );
    }
  }

  return (
    <AuthLayout
      badge="Client Portal"
      headline={
        <>
          Your business,
          <br />
          in one view.
        </>
      }
      description="Track calls, leads, appointments and social performance in real time — the client-facing side of the GoBeeVir Business OS."
      features={[
        'Live call & lead reporting',
        'Social media performance',
        'Appointment calendar',
        'One-click PDF/CSV reports',
      ]}
      cardTitle="Welcome back"
      cardDescription="Sign in to view your live reports and appointments."
      footnote="Secured access · contact your account manager"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <AuthField
          label="Email address"
          type="email"
          inputMode="email"
          autoComplete="username"
          autoFocus
          placeholder="you@company.com"
          icon={<Mail size={14} />}
          error={errors.email?.message}
          {...register('email')}
        />

        <div className="flex flex-col gap-1.5">
          <AuthField
            label="Password"
            type="password"
            autoComplete="current-password"
            placeholder="••••••••••"
            icon={<Lock size={14} />}
            error={errors.password?.message}
            {...register('password')}
          />
          <div className="flex justify-end">
            <AuthLink>Forgot password?</AuthLink>
          </div>
        </div>

        {formError && <AuthError>{formError}</AuthError>}

        <AuthButton isLoading={isSubmitting}>Sign in to dashboard</AuthButton>
      </form>

      {import.meta.env.DEV && (
        <p className="mt-4 text-center font-mono text-[11px] text-auth-dim">
          ramesh@demo.test · Password123!
        </p>
      )}
    </AuthLayout>
  );
}
